import React, { useEffect } from 'react';
import { FileVideo, Book, User, Calendar, Clock, Edit, RefreshCw, Download, Archive, FileText, Paperclip } from 'lucide-react';
import useContentManagementStore from '@/stores/AdminStore/ContentManagementStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import './DetailsTab.css';

interface DetailsTabProps {
  contentId: string;
  onClose: () => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({ contentId, onClose }) => {
  const {
    contentDetails,
    loading,
    error,
    fetchContentDetails,
  } = useContentManagementStore();

  useEffect(() => {
    fetchContentDetails(contentId);
  }, [contentId, fetchContentDetails]);

  if (loading && !contentDetails) {
    return (
      <div className="s-admin-details-container">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !contentDetails) {
    return (
      <div className="s-admin-details-container">
        <ErrorDisplay
          title="Failed to Load Content Details"
          message={error}
          type="error"
          showRetry
          onRetry={() => fetchContentDetails(contentId)}
        />
      </div>
    );
  }

  if (!contentDetails) {
    return null;
  }

  return (
    <div className="s-admin-details-container">
      <div className="s-admin-details-main">
        {/* Box 1: Header and Actions */}
        <div className="s-admin-details-box">
          <div className="s-admin-details-header">
            <div className="s-admin-details-title-section">
              <span><FileVideo size={28} /></span>
              <div>
                <h2>{contentDetails.title}</h2>
                <div className="s-admin-details-badges">
                  <span className={`s-admin-details-type-badge s-admin-details-type-${contentDetails.type.toLowerCase()}`}>
                    {contentDetails.type}
                  </span>
                  <span className={`s-admin-details-status-badge s-admin-details-status-${contentDetails.status.toLowerCase()}`}>
                    {contentDetails.status}
                  </span>
                </div>
              </div>
            </div>
            <button className="s-admin-details-btn-close" onClick={onClose}>✕</button>
          </div>

          <div className="s-admin-details-actions">
            <button className="s-admin-details-btn-action">
              <Edit size={16} />
              Edit
            </button>
            <button className="s-admin-details-btn-action">
              <RefreshCw size={16} />
              Replace File
            </button>
            <button className="s-admin-details-btn-action">
              <Download size={16} />
              Download
            </button>
            <button className="s-admin-details-btn-action s-admin-details-btn-danger">
              <Archive size={16} />
              Archive
            </button>
          </div>
        </div>

        {/* Box 2: Content Metadata */}
        <div className="s-admin-details-box">
          <div className="s-admin-details-metadata-section">
            <h3>Content Metadata</h3>
            <div className="s-admin-details-metadata-grid">
              <div>
                <label>Description</label>
                <p>{contentDetails.description}</p>
              </div>
              <div className="s-admin-details-metadata-row">
                <div>
                  <label>Category</label>
                  <p>{contentDetails.category}</p>
                </div>
                <div>
                  <label>Language</label>
                  <p>{contentDetails.language}</p>
                </div>
              </div>
              <div>
                <label>Tags</label>
                <div className="s-admin-details-content-tags">
                  {contentDetails.tags.map((tag, index) => (
                    <span key={index} className="s-admin-details-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="s-admin-details-metadata-row">
                <div>
                  <label>Owner</label>
                  <p><User size={16} /> {contentDetails.owner}</p>
                </div>
                <div>
                  <label>File Size</label>
                  <p>{contentDetails.fileSize}</p>
                </div>
              </div>
              <div className="s-admin-details-metadata-row">
                <div>
                  <label>Created Date</label>
                  <p><Calendar size={16} /> {contentDetails.createdDate}</p>
                </div>
                <div>
                  <label>Last Updated</label>
                  <p><Clock size={16} /> {contentDetails.lastUpdated}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Box 3: Version History */}
        <div className="s-admin-details-box">
          <div className="s-admin-details-version-section">
            <h3>Version History</h3>
            {contentDetails.versions.map((version, index) => (
              <div key={index} className={`s-admin-details-version-card ${version.current ? 's-admin-details-version-current' : ''}`}>
                <div className="s-admin-details-version-header">
                  <span className="s-admin-details-version-number">Version {version.version}</span>
                  {version.current && <span className="s-admin-details-current-badge">Current</span>}
                </div>
                <p className="s-admin-details-version-meta">Uploaded by {version.uploadedBy}</p>
                <p className="s-admin-details-version-meta">{version.date}</p>
                <p className="s-admin-details-version-meta">Size: {version.size}</p>
                <p className="s-admin-details-version-notes">{version.notes}</p>
                {!version.current && <button className="s-admin-details-btn-restore">Restore</button>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="s-admin-details-sidebar">
        <div className="s-admin-details-sidebar-box">
          <h4>Usage Overview</h4>
          <div className="s-admin-details-usage-section">
            <label>Used In</label>
            {contentDetails.usedIn.map((course, index) => (
              <div key={index} className="s-admin-details-usage-item">
                <Book size={18} />
                <span>{course}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="s-admin-details-sidebar-box">
          <h4>File Information</h4>
          <div className="s-admin-details-file-info">
            <label>Filename</label>
            <p><FileText size={16} />{contentDetails.filename}</p>
            <label>Size</label>
            <p><Paperclip size={16} />{contentDetails.fileSize}</p>
          </div>
        </div>

        <div className="s-admin-details-sidebar-box s-admin-details-stats-box">
          <h4>Quick Stats</h4>
          <div className="s-admin-details-stat-item">
            <span>Total Views</span>
            <strong>{contentDetails.stats.totalViews.toLocaleString()}</strong>
          </div>
          <div className="s-admin-details-stat-item">
            <span>Downloads</span>
            <strong>{contentDetails.stats.downloads.toLocaleString()}</strong>
          </div>
          <div className="s-admin-details-stat-item">
            <span>Used in Courses</span>
            <strong>{contentDetails.stats.usedInCourses}</strong>
          </div>
          <div className="s-admin-details-stat-item">
            <span>Versions</span>
            <strong>{contentDetails.stats.versions}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsTab;
